import {
	useQuery,
	type UseMutationResult,
	type UseQueryOptions,
	type UseQueryResult,
	useMutation,
} from '@tanstack/react-query';
import { SERVER_ROUTES } from '../../../shared/constants/routes.constants';
import { generatePath } from '../../../shared/services/app.shared-service';
import type { ErrorResponse } from '../../../shared/types/error';
import type { SettingsResponseSchemaType } from './../../../shared/schemas/settingsSchemas/settingsResponses.schema';
import { type SettingsEntity } from './../../../shared/types/settings.entity';
import { axiosClient, queryClient } from './api';
import { objectToFormData } from '@/utils/api.utils';
// Query key
const SETTINGS_QUERY_KEY = 'settings';
const baseUrl = [SERVER_ROUTES.SETTINGS.version, SERVER_ROUTES.SETTINGS.BASE];

function updateSettingsCache(updatedSettings: SettingsEntity) {
	queryClient.setQueryData([SETTINGS_QUERY_KEY], updatedSettings);
}

async function fetchSettings(): Promise<SettingsEntity> {
	const response = await axiosClient.get<SettingsResponseSchemaType>(
		generatePath({
			route: [...baseUrl, SERVER_ROUTES.SETTINGS.ROUTES.LIST],
		})
	);
	return response.data.settings as SettingsEntity;
}

export async function updateSettings(
	partialSettings: Partial<SettingsEntity>
): Promise<SettingsEntity> {
	const formData = objectToFormData(partialSettings);

	const response = await axiosClient.put(
		generatePath({
			route: [...baseUrl, SERVER_ROUTES.SETTINGS.ROUTES.UPDATE],
		}),
		formData,
		{
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		}
	);

	return response.data.settings as SettingsEntity;
}

export function useUpdateSettings(): UseMutationResult<
	SettingsEntity,
	ErrorResponse,
	Partial<SettingsEntity>
> {
	return useMutation({
		retry: 0,
		mutationFn: (data) => updateSettings(data),
		onSuccess: (data) => {
			updateSettingsCache(data);
			return data;
		},
	});
}

/**
 * Custom hook to fetch app settings.
 */
export function useSettings(
	options?: UseQueryOptions<SettingsEntity, ErrorResponse>
): UseQueryResult<SettingsEntity, ErrorResponse> {
	return useQuery<SettingsEntity, ErrorResponse>({
		queryKey: [SETTINGS_QUERY_KEY],
		queryFn: fetchSettings,
		...options,
	});
}
